import { formatMoney } from 'accounting'

import swal from 'sweetalert2'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCog,
  faDownload,
  faEnvelope,
  faPaperclip,
  faReply,
  faPen,
} from '@fortawesome/pro-light-svg-icons'
import { faMinusCircle } from '@fortawesome/pro-solid-svg-icons'

import {
  Box, Button, DashboardModuleListItem, IconWithMenu, Link, StatusBadge, Text,
} from '@domain-group/suit-theme'
import { digObject, formatDate } from '@domain-group/javascript-utils'

import AuthenticatedDownload from '@components/AuthenticatedDownload'

import handleCallbackAction from '@functions/handleCallbackAction'

import type { CampaignQuoteModel } from '@models/types'

import useCampaignQuote, { useRelations } from '@hooks/useCampaignQuote'
import type { CampaignQuotePayload } from '@hooks/useCampaignQuote'

import { statusColors } from '@functions/status'

type ConfirmCreateInvoiceParams = {
  createInvoiceFromCampaignQuote: CampaignQuotePayload['callbacks']['createInvoiceFromCampaignQuote'],
}

const confirmCreateInvoice = (params: ConfirmCreateInvoiceParams) => {
  const { createInvoiceFromCampaignQuote } = params

  const requestOptions = {
    include_invoice_items: true,
    include_invoice_creditor: true,
    include_invoice_debtor: true,
  }

  const createInvoicePayload = {
    callbacks: {
      action: createInvoiceFromCampaignQuote,
      buildActionParams: () => [requestOptions],
    },
    shouldRedirect: true,
    toastText: 'Invoice Created',
  }

  swal.fire({
    confirmButtonColor: '#589958',
    confirmButtonText: 'Yes, create it.',
    icon: 'success',
    showCancelButton: true,
    text: 'Are you sure?',
    title: 'Create Invoice',
  })
    .then(({ value }) => {
      if (value){
        // @ts-expect-error TS2345
        handleCallbackAction(createInvoicePayload)
      }
    })
}

type ConfirmVoidCampaignQuoteParams = {
  campaignQuote: CampaignQuoteModel,
  updateCampaignQuote: CampaignQuotePayload['callbacks']['updateCampaignQuote'],
}

const confirmVoidCampaignQuote = (params: ConfirmVoidCampaignQuoteParams) => {
  const { campaignQuote, updateCampaignQuote } = params
  const { id, void: isVoid } = campaignQuote

  const voidQuotePayload = {
    callbacks: {
      action: updateCampaignQuote,
    },
    entityParams: { id, void: !isVoid },
    toastText: 'Quote Voided',
  }

  swal.fire({
    confirmButtonColor: '#DD6B55',
    confirmButtonText: 'Yes, void it.',
    icon: 'warning',
    showCancelButton: true,
    text: 'Are you sure?',
    title: 'Void Quote',
  })
    .then(({ value }) => {
      if (value){
        handleCallbackAction(voidQuotePayload)
      }
    })
}

interface CampaignQuoteListItemProps {
  campaignQuote: CampaignQuoteModel,
}

const CampaignQuoteListItem = (props: CampaignQuoteListItemProps) => {
  const { campaignQuote } = props

  const campaignQuotePayload = useCampaignQuote(campaignQuote, {
    generateUrlsOptions: {
      prefixRoute: `campaigns/${campaignQuote.campaign_id}/edit`,
    },
  })

  const {
    callbacks: {
      createInvoiceFromCampaignQuote,
      emailCampaignQuote,
      updateCampaignQuote,
    },
    campaignQuote: {
      authorized,
      authorization_sent,
      dates,
      id,
      title,
      total,
      void: isVoid,
    },
    isPending,
    status,
    urls: {
      editCampaignQuoteUrl,
      pdfDownloadUrl,
    },
  } = campaignQuotePayload

  const campaignQuoteRelations = useRelations(campaignQuote)
  const { paymentMethod } = campaignQuoteRelations

  // @ts-expect-error TS2345
  const createdDateString = digObject(dates, 'created.date_time', '')
  const formattedCreatedDate = formatDate(createdDateString, 'dd/MM/yyyy HH:mm', "ccc d LLL yyyy 'at' ta")

  const reapproveCampaignQuotePayload = {
    callbacks: {
      action: updateCampaignQuote,
    },
    entityParams: { id, void: !isVoid },
    toastText: 'Quote Reapproved',
  }

  return (
    <Box flexDirection="column" marginBottom="large" variant="white">
      <DashboardModuleListItem
        boxProps={{ borderBottomStyle: 'solid' }}
        href={editCampaignQuoteUrl}
        secondaryText={`Created: ${formattedCreatedDate}`}
        title={title}
      >
        <StatusBadge
          // @ts-expect-error TS7053
          color={(isVoid && 'black') || statusColors[status.key]}
          ghost
          // @ts-expect-error TS2339
          text={(isVoid && 'Void') || status.title}
        />
      </DashboardModuleListItem>

      <Box padding="large">
        <Box justifyContent="center" flexDirection="column">
          <Text color="lightGrey" fontSize="xsmall">
            {/*
             // @ts-expect-error TS2339 */}
            Payment: {paymentMethod.title || 'Not Set'}
          </Text>

          <Text color="bodyFontLightColor" fontSize="xsmall" marginTop="medium">
            {(authorized && 'Authorized') || (authorization_sent && 'Authorization Sent') || 'Authorization not Sent'}
          </Text>
        </Box>

        <Box alignItems="center" marginLeft="auto" width="auto">
          <Text marginRight="large">{formatMoney(total)}</Text>

          <IconWithMenu
            boxProps={{ position: 'relative' }}
            icon={<Button buttonStyle="secondaryUtility" icon={<FontAwesomeIcon icon={faCog} />} size="large" />}
          >
            {/*
             // @ts-expect-error TS2741 */}
            <IconWithMenu.Menu closeOnClick listStyle={{ right: 0 }}>
              {isPending && (
                <Link key="edit" href={editCampaignQuoteUrl}>
                  <FontAwesomeIcon icon={faPen} />
                  <Text marginLeft="medium">Edit Quote</Text>
                </Link>
              )}

              <Link key="email" onClick={() => emailCampaignQuote()}>
                <FontAwesomeIcon icon={faEnvelope} />
                <Text marginLeft="medium">Email Quote</Text>
              </Link>

              <AuthenticatedDownload key="download">
                <Link key="download" href={pdfDownloadUrl}>
                  <FontAwesomeIcon icon={faDownload} />
                  <Text marginLeft="medium">Download Quote</Text>
                </Link>
              </AuthenticatedDownload>

              {!isPending && (
                <Link key="invoice" onClick={() => confirmCreateInvoice({ createInvoiceFromCampaignQuote })}>
                  <FontAwesomeIcon icon={faPaperclip} />
                  <Text marginLeft="medium">Create Invoice</Text>
                </Link>
              )}

              {!isPending && !isVoid && (
                <Link key="void" onClick={() => confirmVoidCampaignQuote({ campaignQuote, updateCampaignQuote })}>
                  <FontAwesomeIcon icon={faMinusCircle} />
                  <Text marginLeft="medium">Void Quote</Text>
                </Link>
              )}

              {!isPending && isVoid && (
                <Link key="reapprove" onClick={() => handleCallbackAction(reapproveCampaignQuotePayload)}>
                  <FontAwesomeIcon icon={faReply} />
                  <Text marginLeft="medium">Reapprove Quote</Text>
                </Link>
              )}
            </IconWithMenu.Menu>
          </IconWithMenu>
        </Box>
      </Box>
    </Box>
  )
}

export default CampaignQuoteListItem
